import { useEffect } from "react";
import { useData } from "vike-react/useData";
import { usePageContext } from "vike-react/usePageContext";
import type { Data } from "./+data";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BlogPostProvider } from "@/components/blog/BlogPostContext";
import { mdxComponents } from "@/components/blog/mdxComponents";
import { BlogBreadcrumb } from "@/components/blog/BlogBreadcrumb";
import { PostHeader } from "@/components/blog/PostHeader";
import { Toc } from "@/components/blog/Toc";
import { BlogEndCta } from "@/components/blog/BlogEndCta";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

// Blog post page (phase 51, UI-SPEC section 2). The MDX body is already resolved on
// pageContext.blogPostContent by the +onBeforeRenderHtml / +onBeforeRenderClient preload hooks,
// so it renders synchronously on the server: no lazy component, no suspense boundary.
// Render is a pure function of build-time data: no browser globals, no clock, no relative time.
export default function Page() {
  const { slug, frontmatter, snapshot, firstDataBlockId, headings, readingMinutes, related } =
    useData<Data>();
  const Content = usePageContext().blogPostContent;
  const { trackBlog } = usePostHogTracking();

  useEffect(() => {
    trackBlog.postViewed({ post_slug: slug, cluster: frontmatter.cluster });
    // Once per post: re-fire only when the slug changes (client navigation between posts).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 pb-16">
        <div className="max-w-3xl mx-auto">
          {/* One provider around the whole column (UI-SPEC section 2 order): MDX links, the end
              CTA and the related cards all fire blog_link_clicked with surface blog_post and this
              post's slug. */}
          <BlogPostProvider value={{ slug, snapshot, surface: "blog_post", firstDataBlockId }}>
            <BlogBreadcrumb title={frontmatter.title} />
            <PostHeader frontmatter={frontmatter} readingMinutes={readingMinutes} />

            <article className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 md:p-8 font-arabic">
              <Toc headings={headings} />
              {Content ? <Content components={mdxComponents} /> : null}
            </article>

            {frontmatter.faq.length > 0 && (
              // Same frontmatter faq array as the FAQPage JSON-LD in +Head.tsx (D-08). Every answer
              // is visible: no accordion, so the structured data matches what a reader sees.
              <section
                aria-labelledby="faq"
                className="mt-12 bg-white rounded-xl border border-gray-200 shadow-sm p-4 md:p-8 font-arabic"
              >
                <h2 id="faq" className="text-xl leading-normal font-bold text-gray-900 mb-4">
                  أسئلة بتتكرر
                </h2>
                <div className="space-y-6">
                  {frontmatter.faq.map((item) => (
                    <div key={item.q}>
                      <h3 className="text-base font-bold text-gray-900 mb-2">{item.q}</h3>
                      <p className="text-base leading-[1.8] text-gray-700">{item.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <BlogEndCta />
            <RelatedPosts posts={related} />
          </BlogPostProvider>
        </div>
      </main>
      <Footer />
    </div>
  );
}
